class DeliverySectionV2 extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    // Default values
    this.label = this.getAttribute('label') || 'Included in Delivery';
    this.deliveryData = this.parseDeliveryData();
    this.isExpanded = false;
    
    this.render();
    this.setupEventListeners();
  }
  
  parseDeliveryData() {
    const deliveryAttr = this.getAttribute('data-delivery');
    if (!deliveryAttr) return [];
    try {
      return JSON.parse(deliveryAttr);
    } catch (e) {
      console.error('Failed to parse delivery data:', e);
      return [];
    }
  }
  
  renderDeliveryItems() {
    if (this.deliveryData.length === 0) {
      return '<p>No delivery items specified.</p>';
    }
    
    return this.deliveryData.map(item => `
      <div class="delivery-item">
        ${item.image ? `<img src="${item.image}" alt="${item.text}" class="delivery-item-image">` : ''}
        <div class="delivery-item-text">${item.text}</div>
      </div>
    `).join('');
  }
  
  static get observedAttributes() {
    return ['label', 'data-delivery'];
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      switch (name) {
        case 'label':
          this.label = newValue;
          this.render();
          this.setupEventListeners();
          break;
        case 'data-delivery':
          this.deliveryData = this.parseDeliveryData();
          this.render();
          this.setupEventListeners();
          break;
      }
    }
  }
  
  render() {
    this.shadowRoot.innerHTML = `
      <style>
        .delivery-section {
          margin: 20px 0;
          border-radius: 4px;
        }

        .delivery-section.expanded .delivery-header {
          border-bottom-color: #000;
        }
        
        .delivery-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 15px;
          cursor: pointer;
          border-bottom: 1px solid #e0e0e0;
        }
        
        .delivery-label {
          font-weight: 600;
        }
        
        .delivery-arrow {
          transition: transform 0.3s ease;
        }
        
        .delivery-content {
          padding: 0 15px;
          max-height: 0;
          opacity: 0;
          overflow: hidden;
          transition: max-height 0.3s ease, opacity 0.3s ease;
        }
        
        .delivery-content.expanded {
          max-height: 500px;
          opacity: 1;
          padding: 15px;
        }
        
        .delivery-arrow.expanded {
          transform: rotate(180deg);
        }
        
        .delivery-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-top: 10px;
        }
        
        .delivery-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        
        .delivery-item-image {
          height: 70px;
          aspect-ratio: 3/2;
          object-fit: contain;
          margin-bottom: 10px;
        }
        
        .delivery-item-text {
          font-size: 14px;
          line-height: 1.4;
        }
      </style>
      <div class="delivery-section">
        <div class="delivery-header" id="delivery-header">
          <span class="delivery-label">${this.label}</span>
          <svg class="delivery-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
        <div class="delivery-content" id="delivery-content">
          <div class="delivery-grid">
            ${this.renderDeliveryItems()}
          </div>
        </div>
      </div>
    `;
  }
  
  setupEventListeners() {
    const deliveryHeader = this.shadowRoot.getElementById('delivery-header');
    const deliveryContent = this.shadowRoot.getElementById('delivery-content');
    const deliveryArrow = this.shadowRoot.querySelector('.delivery-arrow');
    
    if (deliveryHeader && deliveryContent && deliveryArrow) {
      deliveryHeader.addEventListener('click', () => {
        this.toggleExpand();
      });
    }
  }
  
  toggleExpand() {
    this.isExpanded = !this.isExpanded;
    
    const deliverySection = this.shadowRoot.querySelector('.delivery-section');
    const deliveryContent = this.shadowRoot.getElementById('delivery-content');
    const deliveryArrow = this.shadowRoot.querySelector('.delivery-arrow');
    
    if (deliverySection && deliveryContent && deliveryArrow) {
      if (this.isExpanded) {
        deliverySection.classList.add('expanded');
        deliveryContent.classList.add('expanded');
        deliveryArrow.classList.add('expanded');
      } else {
        deliverySection.classList.remove('expanded');
        deliveryContent.classList.remove('expanded');
        deliveryArrow.classList.remove('expanded');
      }
      
      // Dispatch event
      this.dispatchEvent(new CustomEvent('expand-change', { detail: { isExpanded: this.isExpanded } }));
    }
  }
  
  // Public method to get expanded state
  isExpanded() {
    return this.isExpanded;
  }
  
  // Public method to set expanded state
  setExpanded(expanded) {
    if (expanded !== this.isExpanded) {
      this.toggleExpand();
    }
  }
}

customElements.define('delivery-section-v2', DeliverySectionV2);